import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getApiBaseUrl } from "../../utils/apiBaseUrl";
import { createRequestId } from "../../utils/requestId";
import { resolveActiveCompanyId } from "../../utils/companyContextNavigation";
import { showAlert } from "../features/alert/alertSlice";
import {
  handleSessionExpired,
  isAuthFailure,
} from "../../utils/sessionExpiry";

const baseUrl = getApiBaseUrl();

// setting base URL and token in header
const baseQuery = fetchBaseQuery({
  baseUrl: baseUrl,
  keepUnusedDataFor: 30,
  refetchOnMountOrArgChange: true,
  // Required so httpOnly cookies (jwt / connect.sid) are sent and cleared cross-origin in local/dev.
  credentials: "include",
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    const orgId = getState().auth.user?.orgid;
    const activeCompanyId = resolveActiveCompanyId(
      getState().auth.selectedCompany,
      getState().auth.user,
    );
    headers.set("x-request-id", createRequestId());
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
      headers.set("orgid", `${orgId}`);
      if (activeCompanyId) {
        headers.set("selectedcompany", `${activeCompanyId}`);
      }
    }
    return headers;
  },
});

const NETWORK_ERROR_STATUSES = new Set(["FETCH_ERROR", "TIMEOUT_ERROR"]);
const NETWORK_TOAST_COOLDOWN_MS = 3000;
let lastNetworkToastAt = 0;

const maybeShowNetworkErrorToast = (api, result) => {
  if (!NETWORK_ERROR_STATUSES.has(result?.error?.status)) return;

  const now = Date.now();
  if (now - lastNetworkToastAt < NETWORK_TOAST_COOLDOWN_MS) return;

  lastNetworkToastAt = now;
  api.dispatch(
    showAlert({
      message:
        "No internet connection. Please check your network and try again.",
      type: "error",
      timeout: 3000,
    }),
  );
};

const baseQueryWithReauth = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);
  maybeShowNetworkErrorToast(api, result);

  if (isAuthFailure(result?.error)) {
    await handleSessionExpired(api.dispatch);
  }

  return result;
};
export const apiSlice = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "Permission",
    "Search",
    "Books",
    "BookById",
    "BooksLevels",
    "BooksLevelsTask",
    "TaskCompletion",
    "Notifications",
    "Course",
    "CourseDetail",
    "Company",
    "TimeTrails",
    "UserTimeTrails",
    "ActiveTimeTrails",
    "TimeTrailById",
    "TimeTrailChallenges",
    "UserTimeTrailProgress",
    "AdminTimeTrialSubmissions",
    "AdminTimeTrialSubmissionsByTrial",
    "Employee",
    "User",
    "Profile",
    "Points",
    "UserActivity",
    "AdminReports",
    "Promotions",
    "PromotionInvites",
    "PromotionEmailTemplates",
    "PromotionEmailSettings",
    "ItUser",
  ],
  endpoints: (builder) => ({}),
});
